import { useState, useEffect, useRef, useCallback } from 'react'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import type { TableColumn } from '@/types/compatibility'

interface InlineEditCellProps {
  value: unknown
  column: TableColumn
  onSave: (value: unknown) => void
  onCancel: () => void
  autoFocus?: boolean
}

export function InlineEditCell({ 
  value, 
  column, 
  onSave, 
  onCancel, 
  autoFocus = true 
}: InlineEditCellProps) {
  const [inputValue, setInputValue] = useState(value)
  const [showPassword, setShowPassword] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const selectRef = useRef<HTMLSelectElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  
  const dataType = column.dataType ? column.dataType.toLowerCase() : ''
  const isPassword = column.name ? column.name.toLowerCase().includes('password') : false
  const isNullable = column.nullable

  // Type guard functions
  const isString = (val: unknown): val is string => typeof val === 'string'
  const isNumber = (val: unknown): val is number => typeof val === 'number'
  const isBoolean = useCallback((val: unknown): val is boolean => typeof val === 'boolean', [])
  const isNull = useCallback((val: unknown): val is null => val === null, [])
  
  // Convert inputValue to string for form inputs
  const getStringValue = useCallback((): string => {
    if (isNull(inputValue)) return ''
    if (isString(inputValue)) return inputValue
    if (isNumber(inputValue) || isBoolean(inputValue)) return String(inputValue)
    return ''
  }, [inputValue, isNull, isBoolean])

  // Update inputValue when value prop changes (important for inline editing)
  useEffect(() => {
    setInputValue(value)
  }, [value])

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      const el = inputRef.current
      el.focus()
      // Select text when applicable
      if ('select' in el && typeof el.select === 'function') {
        el.select()
      }
    }
  }, [autoFocus])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      onCancel()
    } else if (e.key === 'Tab') {
      e.preventDefault()
      handleSave()
    }
  }

  const handleSave = useCallback(() => {
    let finalValue = inputValue
    
    console.log('💾 InlineEditCell saving:', {
      columnName: column.name,
      dataType,
      originalValue: value,
      inputValue,
      isNullable
    })
    
    // Handle type conversions
    if (dataType.includes('int')) {
      const stringVal = getStringValue()
      if (stringVal === '' || isNull(inputValue)) {
        finalValue = isNullable ? null : 0
      } else {
        const parsed = Number.parseInt(stringVal)
        if (Number.isNaN(parsed)) {
          finalValue = isNullable ? null : 0
        } else {
          finalValue = parsed
        }
      }
    } else if (dataType.includes('decimal') || dataType.includes('float') || dataType.includes('double')) {
      const stringVal = getStringValue()
      if (stringVal === '' || isNull(inputValue)) {
        finalValue = isNullable ? null : 0
      } else {
        const parsed = Number.parseFloat(stringVal)
        if (Number.isNaN(parsed)) {
          finalValue = isNullable ? null : 0
        } else {
          finalValue = parsed
        }
      }
    } else if (dataType.includes('bool')) {
      const stringVal = getStringValue()
      finalValue = stringVal === 'true' || isBoolean(inputValue) && inputValue
    } else if (getStringValue() === '' && isNullable) {
      finalValue = null
    }
    
    console.log('💾 InlineEditCell final value:', finalValue)
    onSave(finalValue)
  }, [inputValue, dataType, isNullable, onSave, column.name, value, getStringValue, isNull, isBoolean])

  const handleBlur = useCallback(() => {
    // Small delay to allow for clicking on show/hide password button
    setTimeout(() => {
      handleSave()
    }, 100)
  }, [handleSave])

  // Boolean input
  if (dataType.includes('bool')) {
    return (
      <div className="w-full">
        <select
          ref={selectRef}
          value={isNull(inputValue) ? 'null' : (isBoolean(inputValue) && inputValue) || getStringValue() === 'true' ? 'true' : 'false'}
          onChange={(e) => {
            const val = e.target.value
            setInputValue(val === 'null' ? null : val === 'true')
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full h-8 px-3 py-1 text-sm border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-gray-800 dark:border-blue-400"
        >
          <option value="true">TRUE</option>
          <option value="false">FALSE</option>
          {isNullable && <option value="null">NULL</option>}
        </select>
      </div>
    )
  }

  // Date input
  if (dataType.includes('date') && !dataType.includes('datetime')) {
    return (
      <div className="w-full">
        <input
          ref={inputRef}
          type="date"
          value={getStringValue()}
          onChange={(e) => { setInputValue(e.target.value) }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full h-8 px-3 py-1 text-sm border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-gray-800 dark:border-blue-400"
        />
      </div>
    )
  }

  // DateTime input
  if (dataType.includes('datetime') || dataType.includes('timestamp')) {
    return (
      <div className="w-full">
        <input
          ref={inputRef}
          type="datetime-local"
          value={(() => {
            const stringVal = getStringValue()
            if (!stringVal) return ''
            try {
              return new Date(stringVal).toISOString().slice(0, 16)
            } catch {
              return ''
            }
          })()}
          onChange={(e) => { setInputValue(e.target.value) }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="w-full h-8 px-3 py-1 text-sm border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-gray-800 dark:border-blue-400"
        />
      </div>
    )
  }

  // Numeric input
  if (dataType.includes('int') || dataType.includes('decimal') || dataType.includes('float') || dataType.includes('double')) {
    return (
      <div className="w-full">
        <input
          ref={inputRef}
          type="number"
          value={getStringValue()}
          onChange={(e) => { setInputValue(e.target.value) }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          step={dataType.includes('int') ? '1' : 'any'}
          placeholder={isNullable ? 'NULL' : undefined}
          className="w-full h-8 px-3 py-1 text-sm border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-gray-800 dark:border-blue-400 text-right tabular-nums"
        />
      </div>
    )
  }

  // Large text input (textarea)
  if (dataType.includes('text') || (column.length && column.length > 255)) {
    return (
      <div className="w-full">
        <textarea
          ref={textareaRef}
          value={getStringValue()}
          onChange={(e) => { setInputValue(e.target.value) }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={isNullable ? 'NULL' : undefined}
          maxLength={column.length || undefined}
          rows={3}
          className="w-full min-h-[6rem] px-3 py-2 text-sm border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-y bg-white dark:bg-gray-800 dark:border-blue-400"
        />
      </div>
    )
  }

  // Password input
  if (isPassword) {
    return (
      <div className="relative w-full">
        <input
          ref={inputRef}
          type={showPassword ? 'text' : 'password'}
          value={getStringValue()}
          onChange={(e) => { setInputValue(e.target.value) }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={isNullable ? 'NULL' : undefined}
          maxLength={column.length || undefined}
          className="w-full h-8 px-3 py-1 pr-10 text-sm border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-gray-800 dark:border-blue-400"
        />
        <button
          type="button"
          onMouseDown={(e) => { e.preventDefault() }} // Prevent blur
          onClick={() => { setShowPassword(!showPassword) }}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 w-6 h-6 flex items-center justify-center"
        >
          {showPassword ? (
            <EyeSlashIcon className="w-4 h-4" />
          ) : (
            <EyeIcon className="w-4 h-4" />
          )}
        </button>
      </div>
    )
  }

  // Default text input
  return (
    <div className="w-full">
      <input
        ref={inputRef}
        type="text"
        value={getStringValue()}
        onChange={(e) => { setInputValue(e.target.value) }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={isNullable ? 'NULL' : undefined}
        maxLength={column.length || undefined}
        className="w-full h-8 px-3 py-1 text-sm border-2 border-blue-500 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-gray-800 dark:border-blue-400 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
      />
    </div>
  )
}
